import React, { useState, useRef } from 'react';
import ReactDOM from 'react-dom'; // Import ReactDOM
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { FaCrop, FaCheck, FaTimes, FaSearchPlus, FaSearchMinus, FaUndo, FaRedo } from 'react-icons/fa'; // Import icons
import './ImageCropper.css';

const ImageCropper = ({ src, onCropComplete, onCropCancel, aspect }) => {
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState(null);
  const [scale, setScale] = useState(1);
  const [rotate, setRotate] = useState(0);
  const imgRef = useRef(null);

  function onImageLoad(e) {
    const { width, height } = e.currentTarget;
    const newCrop = centerCrop(
      makeAspectCrop(
        {
          unit: '%',
          width: 90,
        },
        aspect || 16 / 9,
        width,
        height
      ),
      width,
      height
    );
    setCrop(newCrop);
  }

  // Handle Zoom
  const handleScaleChange = (e) => {
    const newScale = Number(e.target.value);
    setScale(newScale);
  };

  // Handle Rotate
  const handleRotate = (direction) => {
    setRotate((prev) => prev + (direction === 'left' ? -90 : 90));
  };

  const getCroppedImg = async (image, crop, fileName) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('No 2d context');
    }

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const pixelRatio = window.devicePixelRatio || 1;

    canvas.width = Math.floor(crop.width * scaleX * pixelRatio);
    canvas.height = Math.floor(crop.height * scaleY * pixelRatio);

    ctx.scale(pixelRatio, pixelRatio);
    ctx.imageSmoothingQuality = 'high';

    const cropX = crop.x * scaleX;
    const cropY = crop.y * scaleY;

    const rotateRads = rotate * (Math.PI / 180);
    const centerX = image.naturalWidth / 2;
    const centerY = image.naturalHeight / 2;

    ctx.save();

    // 5. Move to crop center
    ctx.translate(-cropX, -cropY);
    // 4. Move to origin
    ctx.translate(centerX, centerY);
    // 3. Rotate
    ctx.rotate(rotateRads);
    // 2. Scale
    ctx.scale(scale, scale);
    // 1. Move back
    ctx.translate(-centerX, -centerY);

    ctx.drawImage(
      image,
      0,
      0,
      image.naturalWidth,
      image.naturalHeight,
      0,
      0,
      image.naturalWidth,
      image.naturalHeight
    );

    ctx.restore();

    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Canvas is empty'));
          return;
        }
        blob.name = fileName;
        resolve(blob);
      }, 'image/jpeg', 0.85);
    });
  };

  const handleCrop = async () => {
    if (completedCrop?.width && completedCrop?.height && imgRef.current) {
      try {
        const croppedImageBlob = await getCroppedImg(
          imgRef.current,
          completedCrop,
          'cropped-image.jpg'
        );
        onCropComplete(croppedImageBlob);
      } catch (e) {
        console.error("Gagal melakukan crop:", e);
      }
    }
  };

  // Gunakan Portal untuk merender modal di body, agar tidak tertutup elemen lain (stacking context issue)
  return ReactDOM.createPortal(
    <div className="image-cropper-overlay">
      <div className="image-cropper-modal">
        <div className="cropper-header">
          <h3><FaCrop style={{ marginRight: '8px' }} /> Sesuaikan Gambar</h3>
        </div>

        <div className="cropper-body">
          <ReactCrop
            crop={crop}
            onChange={(_, percentCrop) => setCrop(percentCrop)}
            onComplete={(c) => setCompletedCrop(c)}
            aspect={aspect}
            className="react-crop-container"
          >
            <img
              ref={imgRef}
              alt="Crop me"
              src={src}
              onLoad={onImageLoad}
              style={{ transform: `scale(${scale}) rotate(${rotate}deg)` }}
              crossOrigin="anonymous"
            />
          </ReactCrop>
        </div>

        <div className="cropper-controls">
          {/* Zoom Control */}
          <div className="control-group">
            <FaSearchMinus className="control-icon" />
            <input
              type="range"
              min={1}
              max={3}
              step={0.1}
              value={scale}
              onChange={handleScaleChange}
              className="zoom-slider"
            />
            <FaSearchPlus className="control-icon" />
          </div>

          {/* Rotate Controls */}
          <div className="control-group">
            <button className="btn-icon" onClick={() => handleRotate('left')} title="Putar Kiri">
              <FaUndo />
            </button>
            <button className="btn-icon" onClick={() => handleRotate('right')} title="Putar Kanan">
              <FaRedo />
            </button>
          </div>
        </div>

        <div className="cropper-footer">
          <button className="btn-cancel" onClick={onCropCancel}>
            <FaTimes /> Batal
          </button>
          <button className="btn-save" onClick={handleCrop}>
            <FaCheck /> Simpan & Gunakan
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ImageCropper;
